import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { RoutingTabComponent } from '../../../components/ui/tabs/routing-tab/routing-tab.component';
import { SettingsService } from '../../../services/settings/settings.service';
import { TBlkCxpRole } from '../../../types/settingsDataModule';
import { hasAnyRole, ORGANIZATION_TAB_ROLES, TEAM_TAB_ROLES } from './settings-roles';

interface SettingsTab {
  title: string;
  link: string;
}

/** What each tab holds, in one line under the heading. */
const TAB_DESCRIPTIONS: Record<string, string> = {
  profile: 'Your name, photo and password, and what your roles let you do.',
  organization: 'How your organization appears, and the sender IDs you send from.',
  'user-management': 'Who can sign in to Black Konnect and what each person can do.',
  notifications: 'Planned email alerts. Not available yet.',
  'test-contacts': 'Phones that receive your test messages before a real send.',
};

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [RoutingTabComponent, RouterOutlet],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css',
})
export class SettingsComponent implements OnInit {
  private settingsService = inject(SettingsService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  tabs: SettingsTab[] = [];

  /**
   * Changes whenever the tab list does. The tab strip is keyed on it so it is
   * rebuilt and highlights the right tab once the roles arrive.
   */
  tabsKey = '';

  /** The child route segment, e.g. "user-management". */
  section = 'profile';

  // Notifications has no backend yet, so it is not offered here; its URL
  // still opens a page that says so.
  private readonly allTabs: SettingsTab[] = [
    { title: 'Profile', link: '/organization/settings/profile' },
    { title: 'Organization', link: '/organization/settings/organization' },
    { title: 'Users', link: '/organization/settings/user-management' },
    { title: 'Test numbers', link: '/organization/settings/test-contacts' },
  ];

  get description(): string {
    return TAB_DESCRIPTIONS[this.section] ?? TAB_DESCRIPTIONS['profile'];
  }

  ngOnInit(): void {
    this.readSection(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => this.readSection(e.urlAfterRedirects));

    this.settingsService.getUserRoles
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userRoles: TBlkCxpRole[]) => {
        const canSeeOrganization = hasAnyRole(userRoles, ORGANIZATION_TAB_ROLES);
        const canSeeTeam = hasAnyRole(userRoles, TEAM_TAB_ROLES);

        this.tabs = this.allTabs.filter((tab) => {
          if (tab.link.endsWith('/organization')) return canSeeOrganization;
          if (tab.link.endsWith('/user-management')) return canSeeTeam;
          if (tab.link.endsWith('/test-contacts')) return canSeeTeam;
          return true;
        });
        this.tabsKey = this.tabs.map((tab) => tab.link).join('|');
      });
  }

  private readSection(url: string): void {
    const path = (url || '').split(/[?#]/)[0];
    const match = path.match(/\/settings\/([^/]+)/);
    this.section = match ? match[1] : 'profile';
  }
}
